<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { catalogFilterKey } from '@/domain/catalog/catalog-filter-copy';

  /**
   * Nothing matches (plan §5.16): a search offers to clear it or to create
   * the product; a filter says what is fine and offers «Ver todos».
   */
  const { state, data, access, url } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const query = computed(() => data.debouncedQuery.value.trim());

  const filtered = computed(() => state.filter !== 'all');

  const title = computed(() => {
    if (query.value)
      return t(
        filtered.value
          ? 'menuPage.states.noResultsFiltered'
          : 'menuPage.states.noResults',
        { q: query.value }
      );
    return t(`menuPage.states.filterEmpty.${catalogFilterKey(state.filter)}`);
  });

  function createFromSearch() {
    url.openNewItem(state.activeCategoryId, query.value);
  }
</script>

<template>
  <div class="v2-catalog-no-results">
    <PosUiEmptyState
      :icon="query ? 'magnify' : 'check-circle-outline'"
      :title="title"
      :description="
        query && !filtered ? t('menuPage.states.noResultsHint') : undefined
      "
    >
      <template #action>
        <div class="no-results-actions">
          <PosUiButton
            v-if="query"
            color="neutral"
            variant="outline"
            @click="url.clearSearch()"
            >{{ t('menuPage.states.clearSearch') }}</PosUiButton
          >
          <PosUiButton
            v-if="query && !filtered && access.canManage"
            class="action-create"
            color="neutral"
            icon="plus"
            @click="createFromSearch"
            >{{
              t('menuPage.states.createFromSearch', { q: query })
            }}</PosUiButton
          >
          <PosUiButton
            v-if="filtered"
            color="neutral"
            variant="outline"
            @click="url.setFilter('all')"
            >{{ t('menuPage.states.seeAll') }}</PosUiButton
          >
        </div>
      </template>
    </PosUiEmptyState>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-no-results {
    @apply tw-mx-auto tw-mt-6 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;
    max-width: 640px;

    // Margins, not flex gap (Chrome < 84).
    .no-results-actions {
      @apply tw-flex tw-flex-wrap tw-justify-center;

      > * {
        @apply tw-m-1;
      }
    }
  }
</style>
